import { GoalLinkRow, ID } from '@griply/types';

export type ResolveMovedGoalLinksOptions = {
  currentParentIds: ID[];
  currentLifeAreaIds: ID[];
  // The link being moved — the one the picker highlighted. Both absent: the goal had none.
  movedParentId?: ID;
  movedLifeAreaId?: ID;
  targetParentId?: ID | null;
  targetLifeAreaId?: ID | null;
};

export type MovedGoalLinks = {
  parentIds: ID[];
  lifeAreaIds: ID[];
};

const withTarget = (ids: ID[], targetId?: ID | null): ID[] =>
  targetId ? Array.from(new Set([...ids, targetId])) : ids;

// Only the moved link changes; every other parent and life area stays.
export const resolveMovedGoalLinks = ({
  currentParentIds,
  currentLifeAreaIds,
  movedParentId,
  movedLifeAreaId,
  targetParentId,
  targetLifeAreaId,
}: ResolveMovedGoalLinksOptions): MovedGoalLinks => ({
  parentIds: withTarget(
    currentParentIds.filter((id) => id !== movedParentId),
    targetParentId,
  ),
  lifeAreaIds: withTarget(
    currentLifeAreaIds.filter((id) => id !== movedLifeAreaId),
    targetLifeAreaId,
  ),
});

/*
  "Move to…" is a move, not an added link: whatever the goal hung under before, it hangs under the
  one chosen target afterwards. Every other parent and life area goes. Dragging is the other rule —
  there only the link the row sits under is replaced, which is what resolveMovedGoalLinks does.
*/
export const resolveReplacedGoalLinks = ({
  targetParentId,
  targetLifeAreaId,
}: Pick<
  ResolveMovedGoalLinksOptions,
  'targetParentId' | 'targetLifeAreaId'
>): MovedGoalLinks => ({
  parentIds: targetParentId ? [targetParentId] : [],
  lifeAreaIds: targetLifeAreaId ? [targetLifeAreaId] : [],
});

export type ResolveMovedLinkIdsOptions = {
  // The link the row sits under. Absent when the caller has no row context to offer.
  row?: GoalLinkRow;
  currentParentIds: ID[];
  currentLifeAreaIds: ID[];
};

// Which of a goal's links a move replaces: the one its row sits under. Without a row there is
// nothing to go on, so the goal's first parent wins and its life area only when it has none.
export const resolveMovedLinkIds = ({
  row,
  currentParentIds,
  currentLifeAreaIds,
}: ResolveMovedLinkIdsOptions): {
  movedParentId?: ID;
  movedLifeAreaId?: ID;
} => {
  if (row) {
    return { movedParentId: row.goalId, movedLifeAreaId: row.lifeAreaId };
  }

  const [movedParentId] = currentParentIds;

  return {
    movedParentId,
    movedLifeAreaId: movedParentId ? undefined : currentLifeAreaIds[0],
  };
};
